import { Box, FormHelperText } from '@mui/material'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FlexBox } from 'components/Box'
import { SaveButton, TagsField } from 'components/Form'
import Hint from 'components/Hint'
import { toFirstUrlErrorMessage } from 'features/yieldbirdWrapperAgent/utils/toFirstUrlErrorMessage'

import type { Schema } from './schema'

const SUBMIT_BUTTON_OFFSET_TOP: number = 1.25

type Props = {
  formId: string
  isPending: boolean
  remainingUrls?: Schema['urls']
}

export const Form = ({ formId, isPending, remainingUrls }: Props): React.JSX.Element => {
  const { t } = useTranslation('features/yieldbirdWrapperAgent', { keyPrefix: 'form' })
  const {
    control,
    formState: { errors, isValid },
    setValue,
  } = useFormContext<Schema>()

  const urlErrorMessage: string | undefined = toFirstUrlErrorMessage(errors.urls)

  useEffect(() => {
    if (remainingUrls !== undefined) {
      setValue('urls', remainingUrls)
    }
  }, [remainingUrls, setValue])

  return (
    <FlexBox
      axis='x'
      sx={{ alignItems: 'flex-start', gap: 2 }}
    >
      <Box>
        <TagsField
          control={control}
          disabled={isPending}
          label={t('urls.label')}
          name='urls'
          placeholder={t('urls.placeholder')}
          width='lg'
        />

        {urlErrorMessage && <FormHelperText error>{urlErrorMessage}</FormHelperText>}

        <Hint content={t('urls.hint')} />
      </Box>

      <Box sx={{ pt: SUBMIT_BUTTON_OFFSET_TOP }}>
        <SaveButton
          formId={formId}
          loading={isPending}
          text={t('submit')}
          touched
          valid={isValid}
        />
      </Box>
    </FlexBox>
  )
}
